import CollapsableBox from "../collapsableBox/CollapsableBox";
import "./blackout-periods.css";
import React, {createContext, useEffect, useMemo, useRef, useState} from "react";
import {Button, Modal} from "antd";
import {BlackoutDateProps, DAY} from "../../types/BlackoutPeriod";
import NewBlackoutPeriodModal from "../modals/blackoutPeriodModals/NewBlackoutPeriodModal";
import {hourToAMPM} from "../../utils/hourToAMPM";
import BlackoutPeriodDay from "./BlackoutPeriodDay";
import useAppContext from "../../hooks/useAppContext";
import {formatDate, formatDateNoTime} from "../../utils/friendlyDate";
import blackoutPeriodDay from "./BlackoutPeriodDay";
import {BLACKOUT_PERIODS_COLORS} from "../charts/consts/COLOR_SETS";

interface BlackoutPeriodContextProps {
    onBlackoutPeriodClick: (id: number | undefined) => () => void;
    onHoverIn: (num: number) => () => void;
    onHoverOut: (num: number) => () => void;
    hoveredId: number | null;
    periodColors: {[key: string]: string},
}

export const BlackoutPeriodContext = createContext<BlackoutPeriodContextProps>({
    onBlackoutPeriodClick: () => () => null,
    onHoverIn: () => () => null,
    onHoverOut: () => () => null,
    hoveredId: null,
    periodColors: {},
});

export interface BlackoutPeriodsProps {
    isInitiallyOpen?: boolean;
}

const BlackoutPeriods = ({ isInitiallyOpen = false }: BlackoutPeriodsProps) => {
    const [isNewModalOpen, setIsNewModalOpen] = useState(false);
    const [isLoadingNewPeriod, setIsLoadingNewPeriod] = useState(false);
    const [editedBlackoutPeriodId, setEditedBlackoutPeriodId] = useState<number | null>(null);
    const { data: { blackoutPeriods } } = useAppContext();
    const [hoveredId, setHoveredId] = useState<number | null>(null);
    const dayPeriods = blackoutPeriods.data.filter((period: BlackoutDateProps) => period.fromDay && period.toDay && !period.fromDate && !period.toDate);

    const totalDayPeriods = blackoutPeriods.data.filter((period: BlackoutDateProps) => period.fromDay && period.toDay && !period.fromDate && !period.toDate).length;
    const yearPeriods = blackoutPeriods.data.filter((period: BlackoutDateProps) => !period.fromDay && !period.toDay && period.fromDate && period.toDate);
    const shouldShowWeeklyPeriods: boolean = totalDayPeriods > 0;
    const shouldShowYearlyPeriods: boolean = yearPeriods.length > 0;
    const sectionRef = useRef<HTMLDivElement | null>(null);

    const periodColors = useMemo(() => {
        const colors: { [key: string]: string } = {};
        blackoutPeriods.data.forEach((dayPeriod: any) => {
            const colorCountFix = Math.floor(Object.keys(colors).length / BLACKOUT_PERIODS_COLORS.length);
            const nextKey = Object.keys(colors).length - BLACKOUT_PERIODS_COLORS.length * colorCountFix;
            colors[dayPeriod.id] = BLACKOUT_PERIODS_COLORS[nextKey];
        });
        return colors;
    }, [dayPeriods]);

    useEffect(() => {
        if(isInitiallyOpen) {
            sectionRef.current?.scrollIntoView({ behavior: 'smooth' });
        }
    }, [isInitiallyOpen]);

    const onAddBlackoutPeriodClick = (event: React.MouseEvent<HTMLElement>) => {
        event.stopPropagation();
        setIsNewModalOpen(true);
    }

    const onCloseModal = () => {
        setIsNewModalOpen(false);
        setEditedBlackoutPeriodId(null);
    }

    const onBlackoutPeriodClick = (id: number | undefined) => () => {
        setEditedBlackoutPeriodId(id || null);
        setIsNewModalOpen(true);
    }

    const onHoverIn = (bpId?: number) => () => {
        bpId && setHoveredId(bpId);
    }

    const onHoverOut = (bpId?: number) => () => {
        if(hoveredId === bpId) {
            setHoveredId(null);
        }
    }

    return (
        <CollapsableBox title="blackout periods" isInitiallyOpen={isInitiallyOpen} actions={[<Button key="add-blackout-period-button" type="primary" size="large" onClick={onAddBlackoutPeriodClick}>Add Blackout Period</Button>]}>
            <div ref={sectionRef} className="blackout-periods-container">
                <div className="description">
                    <div>
                        Set blackout periods so your clients won't get messages from you during the night, the holidays, or any other inappropriate time.
                        For example, you can avoid messaging your clients between 10PM to 8AM.
                    </div>
                    <div>
                        Every message that was scheduled to be automatically sent during the blackout period, will be delayed and sent once the blackout period is over.
                        We already set default values, but you can change those to your preferences.
                    </div>

                </div>
                {shouldShowWeeklyPeriods ?
                    <>
                        <div className="subtitle">
                            Weekly Blackout Periods
                        </div>

                        <div className="schedule-table">
                            <div className="schedule-table-row schedule-table-head">
                                <div className="schedule-table-col">monday</div>
                                <div className="schedule-table-col">tuesday</div>
                                <div className="schedule-table-col">wednesday</div>
                                <div className="schedule-table-col">thursday</div>
                                <div className="schedule-table-col">friday</div>
                                <div className="schedule-table-col">saturday</div>
                                <div className="schedule-table-col">sunday</div>
                            </div>

                            <div className="schedule-table-row">
                                <BlackoutPeriodContext.Provider value={{
                                    onBlackoutPeriodClick,
                                    onHoverIn,
                                    onHoverOut,
                                    hoveredId,
                                    periodColors
                                }}>
                                    <div className="schedule-table-col"><BlackoutPeriodDay dayPeriods={dayPeriods} day={DAY.MONDAY} /></div>
                                    <div className="schedule-table-col"><BlackoutPeriodDay dayPeriods={dayPeriods} day={DAY.TUESDAY} /></div>
                                    <div className="schedule-table-col"><BlackoutPeriodDay dayPeriods={dayPeriods} day={DAY.WEDNESDAY} /></div>
                                    <div className="schedule-table-col"><BlackoutPeriodDay dayPeriods={dayPeriods} day={DAY.THURSDAY} /></div>
                                    <div className="schedule-table-col"><BlackoutPeriodDay dayPeriods={dayPeriods} day={DAY.FRIDAY} /></div>
                                    <div className="schedule-table-col"><BlackoutPeriodDay dayPeriods={dayPeriods} day={DAY.SATURDAY} /></div>
                                    <div className="schedule-table-col"><BlackoutPeriodDay dayPeriods={dayPeriods} day={DAY.SUNDAY} /></div>
                                </BlackoutPeriodContext.Provider>
                            </div>
                        </div>
                    </>
                : null}
                {shouldShowYearlyPeriods ?
                    <>
                        <div className="subtitle">
                            Yearly Blackout Periods
                        </div>
                        <div className="yearly-dates">
                            {yearPeriods.map((period: BlackoutDateProps, index: number) =>
                                <div key={`${period.fromDate}-${period.toDate}-${period.fromTime}-${period.toTime}${index}`} onClick={onBlackoutPeriodClick(period.id)} style={{ backgroundColor: period?.id ? periodColors[period.id] : '#000' }} >
                                    <div className="title">{period.title ? `${period.title}` : ''}</div>
                                    <div className="date">From {formatDateNoTime(new Date(period.fromDate!))} {hourToAMPM(period.fromTime)}</div>
                                    <div className="date">To {formatDateNoTime(new Date(period.toDate!))} {hourToAMPM(period.toTime)}</div>
                                </div>
                            )}
                        </div>
                    </>
                : null}
            </div>

            <NewBlackoutPeriodModal isOpen={isNewModalOpen} onClose={onCloseModal} blackoutPeriodRecordId={editedBlackoutPeriodId} />
        </CollapsableBox>
    );
}

export default BlackoutPeriods;